import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQueries } from '@tanstack/react-query'
import { postNamedQuery } from '../../../service'
import { useStatusReportSupplementalData } from '../../../api/groups'
import { StatusReportItemSupplementalContentContext } from './StatusReportItemSupplementalContentContext'
import { useStatusReportContext } from './statusReportContext'
import { indexStatusReportItemsById } from './supplementalLayouts/pipeline/indexStatusReportItemsById'
import { expandSupplementalPipelineSources } from './supplementalLayouts/pipeline/expandSupplementalPipelineSources'
import { buildSupplementalPipelineExecuteVariables } from './supplementalLayouts/pipeline/resolveSupplementalPipelineVariables'
import { filterPipelineRowsForLine, wrapMatchedRowsForDisplay } from './supplementalLayouts/pipeline/supplementalRowMatch'

function StatusReportItemSupplementalContentProvider ({ children }) {
  const { report, config } = useStatusReportContext()
  const statusReportId = report?.statusReportId
  const clientId = report?.levelId
  const supplementalDataCode = config?.supplementalDataCode
  const shouldLoadSupplemental = !!(supplementalDataCode || report?.hasSupplementalData)

  const { data: configData, isLoading: configLoading } = useStatusReportSupplementalData(statusReportId, {
    supplementalDataCode,
    enabled: shouldLoadSupplemental
  })

  const itemByStatusReportItemId = useMemo(
    () => indexStatusReportItemsById(report?.items ?? []),
    [report?.items]
  )

  const pipelineSources = useMemo(() => {
    const raw = configData?.supplementalSources ?? []
    return expandSupplementalPipelineSources(raw, itemByStatusReportItemId)
      .filter(s => s.sourceType === 'pipeline' && s.sourceKey)
  }, [configData?.supplementalSources, itemByStatusReportItemId])

  const byPipeline = useMemo(() => {
    const map = {}
    for (const s of pipelineSources) {
      const key = s.sourceKey
      if (!map[key]) map[key] = []
      map[key].push(s)
    }
    return map
  }, [pipelineSources])

  const pipelineQueries = useQueries({
    queries: Object.entries(byPipeline).map(([pipelineCode, items]) => ({
      queryKey: [
        'status-report-supplemental-pipeline',
        statusReportId,
        supplementalDataCode ?? '_all',
        pipelineCode,
        items[0]?.pipelineVariables ?? null
      ],
      queryFn: async () => {
        const templateVars = items[0]?.pipelineVariables ?? {}
        const variables = buildSupplementalPipelineExecuteVariables(templateVars, {
          clientId,
          statusReportId
        })
        const { data } = await postNamedQuery('coreData', 'execute-data-pipeline', {
          pipelineCode,
          variables
        })
        return { pipelineCode, items, result: data }
      },
      enabled: !!statusReportId && !!clientId && shouldLoadSupplemental && !!pipelineSources.length
    }))
  })

  const pipelineDataSignal = pipelineQueries.map(q => q.dataUpdatedAt).join(',')
  const supplementalData = useMemo(() => {
    const merged = {}
    const pushSource = (itemId, itemData, matchedItem, pipelineCode) => {
      const key = String(itemId)
      if (!merged[key]) merged[key] = { sources: [] }
      merged[key].sources.push({
        supplementalSourceId: matchedItem?.supplementalSourceId,
        sourceType: 'pipeline',
        sourceKey: pipelineCode,
        displayConfig: matchedItem?.displayConfig ?? null,
        data: itemData
      })
    }

    for (const q of pipelineQueries) {
      const result = q.data?.result
      if (!result) continue
      const pipelineCode = q.data.pipelineCode
      const items = q.data.items ?? []
      const rows = result?.data
      if (!Array.isArray(rows) || !items.length) continue

      for (const src of items) {
        const line = itemByStatusReportItemId.get(src.statusReportItemId)
        if (!line) continue
        const matchConfig = src.matchConfig
        const matched = filterPipelineRowsForLine(rows, matchConfig, line)
        if (!matched.length) continue
        const itemData = wrapMatchedRowsForDisplay(matched, src.displayConfig)
        pushSource(src.statusReportItemId, itemData, src, pipelineCode)
      }
    }
    return merged
  // pipelineQueries is read inside but intentionally excluded from deps; pipelineDataSignal
  // (joined dataUpdatedAt timestamps) changes whenever any query's data changes, which is the
  // only case where the derived supplementalData should recompute.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineDataSignal, itemByStatusReportItemId])

  const isLoading = configLoading || pipelineQueries.some(q => q.isLoading)

  const value = useMemo(() => ({
    getItemSupplementalData: (statusReportItemId) => supplementalData[statusReportItemId] ?? null,
    isLoading
  }), [supplementalData, isLoading])

  return (
    <StatusReportItemSupplementalContentContext.Provider value={value}>
      {children}
    </StatusReportItemSupplementalContentContext.Provider>
  )
}

StatusReportItemSupplementalContentProvider.propTypes = {
  children: PropTypes.node
}

export default StatusReportItemSupplementalContentProvider
